import { useMemo } from 'react'
import { AssetProperties } from '../AssetProperties'
import { useAssetBaseProperties } from '../../hooks/useQueries'
import { useAssetCoreRows } from '../../hooks/useAssetCoreRows'
import { DATA_CATEGORIES, DATA_CATEGORY_ORDER, baseCodesFor } from './baseProperties'

/**
 * The Data tab — an asset's property registry, coherent for every asset type.
 *
 * The expected base-property set is resolved from the API
 * (GET …/assets/{uid}/base-properties): it is country-aware (the universal '*'
 * socle plus the asset's jurisdiction profile) and carries, per property, a
 * requirement level and an expected_source hint (assigned / diagnosed /
 * measured / declared). Base properties are shown even when empty so the whole
 * expected set stays visible and fillable. While the socle loads, a curated
 * per-type fallback keeps the tab populated.
 *
 * Unlike the previous revision, the Conformité (compliance) properties are
 * surfaced here as part of the socle — the Compliance tab still owns the
 * obligation lifecycle, but the underlying fields are visible and editable in
 * Data.
 */
export function AssetDataTab({
    org,
    assetUid,
    assetTypeCode,
}: {
    org: string
    assetUid: string
    assetTypeCode?: string
}) {
    const { data: base } = useAssetBaseProperties(org, assetUid)

    // Type / external code / lifecycle dates lead the Identité section. Shared
    // with the Properties tab so both registries edit them identically.
    const { coreRows, onSaveCoreRow } = useAssetCoreRows(org, assetUid)

    const { baseCodes, categoryOrder, categoryCodes, baseMeta, restrict } = useMemo(() => {
        const sections = base?.sections ?? []
        if (sections.length === 0) {
            // Fallback while the API socle loads (curated, category-grouped).
            return {
                baseCodes: baseCodesFor(assetTypeCode),
                categoryOrder: [...DATA_CATEGORY_ORDER] as string[],
                categoryCodes: DATA_CATEGORIES,
                baseMeta: {} as Record<string, { requirement?: string; expectedSource?: string; section?: string }>,
                restrict: false,
            }
        }
        const codes: string[] = []
        const order: string[] = []
        const meta: Record<string, { requirement?: string; expectedSource?: string; section?: string }> = {}
        for (const s of sections) {
            if (s.category) order.push(s.category)
            for (const p of s.properties ?? []) {
                if (!p.code) continue
                codes.push(p.code)
                meta[p.code] = { requirement: p.requirement, expectedSource: p.expected_source ?? undefined, section: s.category }
            }
        }
        return { baseCodes: codes, categoryOrder: order, categoryCodes: order, baseMeta: meta, restrict: true }
    }, [base, assetTypeCode])

    return (
        <div className="@container" data-testid="asset-data-tab">
            <AssetProperties
                org={org}
                assetUid={assetUid}
                variant="table"
                groupByCategory
                categoryOrder={categoryOrder}
                // When restricted to the socle, don't filter values by category:
                // a base code's real category (e.g. gross_floor_area = surfaces)
                // may differ from its display section, and we still need its value.
                categoryCodes={restrict ? undefined : categoryCodes}
                baseCodes={baseCodes}
                baseMeta={baseMeta}
                restrictToBaseCodes={restrict}
                hideEmptyByDefault
                coreRows={coreRows}
                onSaveCoreRow={onSaveCoreRow}
            />
        </div>
    )
}
